import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getMyCV } from "@service/cv";
import { applicantRequestNewAssessmentLink, applicantStartInterview, getJobApplicantStatus, getJobDetail, updateApplicantStatusAssessmentCompletion } from "@service/job";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import { Job, JobWorkAuthorizationMetadata } from "@type/job";
import { CVDataInfo } from "@type/cv";
import { getAssessment, getAssessmentInfo, getUserInterviewSubmission, recordFormAssessmentStarted, recordEligibilityCheckStarted } from "@service/assessment";
import { FormAssessment, InterviewAssessment } from "@type/assessments";
import { getAssessmentFormStatus, getInterviewStatusV2, getVideo, updateInterviewStatusV2 } from "@service/interview";
import { CustomInterviewSection } from "@type/videoInterview";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import { DailyCall } from "@daily-co/daily-js";
import { formatCV } from "@util/string";
import { interviewStatusAborted, interviewStatusComplete, interviewStatusPartial } from "@constant/app";
import { catchErrorLog } from "@util/sentry";
import { Company } from "@type/company";
import { getCompanyInfo } from "@service/user";
import { trackEvent } from "@util/mixpanel";
import { SnackbarProvider } from "@hook/use-snackbar";
import { useQuery } from "@util/useQuery";
import { getCookie, removeCookie, setCookie } from "@util/cookie";
import { ArrowRightIcon, CalendarClockIcon, CheckCircle2Icon, ClockIcon, ExternalLinkIcon, HandshakeIcon, Link2, Link2OffIcon, Loader2Icon, Mail, UploadIcon, VideoIcon } from "lucide-react";
import { confirmFlowStep, getPendingAssessments, PendingAssessmentsResponse, PendingFlowStep, uploadFlowDocument } from "@service/hiring-flow";
import { documentLabel } from "@type/hiring-flow";
import { Button } from "@component/button";
import { getFirstName } from "@util/name";
import { Card, CardContent } from "@component/card";
import { countDayNowBetweenDate, parseFormat } from "@util/date";
import Quiz from "@page/apply-now-quiz";
import WorkAuthorization from "@page/apply-now-work-authorization";
import VoiceInterviewV2 from "@container/voice-interview-test-v2";
import CustomVoiceInterviewV2 from "@container/custom-voice-interview-v2";
import ELVoiceInterview from "@container/el-voice-interview";
import InterviewShortCard from "@container/interview-short-card";
import SWEExperienceSurvey from "@container/eligibility-check-job";
import { Checkbox } from "@component/checkbox";

// ---------------------------------------------------------------------------
// Welcome screen helpers & sub-components
// ---------------------------------------------------------------------------
function formatWelcomeMins(mins: number): string {
    return `~${mins} min${mins !== 1 ? "s" : ""}`;
}

function getEligibilityQuestionCount(metadata?: JobWorkAuthorizationMetadata): number {
    if (!metadata) return 0;
    return [
        metadata.collect_employment_status,
        metadata.collect_expected_salary,
        metadata.collect_start_availability,
        metadata.collect_current_salary,
        metadata.collect_relocation_willingness,
        metadata.collect_weekend_shift_willingness,
    ].filter(Boolean).length;
}

function getCompanyInitials(name: string): string {
    return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

// ---------------------------------------------------------------------------
// Flag-driven legacy step order (#3066 rollout)
// ---------------------------------------------------------------------------
// Legacy screen numbers for the flow step types the legacy machine renders.
const LEGACY_SCREEN_BY_FLOW_TYPE: Record<string, number> = { ai_interview: 2, form: 3, eligibility_check: 4 };

function legacyScreenLabel(screen: number): string {
    return screen === 2 ? "AI Interview" : screen === 3 ? "Form" : "Eligibility Check";
}

// When the job detail carries company_hiring_flow_enabled === true AND a
// genuinely stored flow, the legacy interview/form/eligibility machine runs
// its screens in the flow's order instead of the hardcoded 2→3→4. The BE
// resolves `hiring_flow` to the ourteam Default even for uncustomized jobs, so
// "genuinely stored" means hiring_flow_is_custom === true (the read's explicit
// signal, same as the candidate drawer) OR a template that isn't the untouched
// default (same discrimination as the job-form prefill). Flag OFF/absent ⇒
// empty ⇒ untouched legacy order.
function hiringFlowScreenOrder(job: Job | undefined): number[] {
    if (job?.company_hiring_flow_enabled !== true) return [];
    if (!job.hiring_flow) return [];
    if (job.hiring_flow.template === "ourteam_default" && job.hiring_flow_is_custom !== true) return [];
    const order: number[] = [];
    for (const step of job.hiring_flow.steps || []) {
        const screen = LEGACY_SCREEN_BY_FLOW_TYPE[step.type];
        // ONE screen per type — a flow holding several forms/interviews keeps
        // the first occurrence for ordering.
        if (screen && !order.includes(screen)) order.push(screen);
    }
    return order;
}

type WelcomeStepPill = { label: string; time: string; tooltip: string; wrapTooltip?: boolean };

function WelcomeCompanyAvatar({ logoUrl, companyName, isLoading }: { logoUrl: string | null; companyName: string; isLoading: boolean }) {
    if (isLoading) {
        return <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-neutral-100 animate-pulse flex-shrink-0" />;
    }
    if (logoUrl) {
        return (
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex-shrink-0 overflow-hidden bg-white border border-neutral-100 flex items-center justify-center">
                <img
                    src={logoUrl}
                    alt={`${companyName} logo`}
                    onError={(e) => {
                        e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/company_placeholder.png`;
                    }}
                    className="w-full h-full object-contain p-1"
                />
            </div>
        );
    }
    return (
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-base sm:text-lg font-semibold flex-shrink-0">
            {getCompanyInitials(companyName)}
        </div>
    );
}

function WelcomeJobCard({ jobTitle, companyName, companyDisplay, logoUrl, daysLeft, expiryDate, jobId, invitedDate, department, isLoading }: {
    jobTitle: string; companyName: string; companyDisplay: string; logoUrl: string | null;
    daysLeft: number; expiryDate: string; jobId: string; invitedDate: string; department: string; isLoading: boolean;
}) {
    const urgent = daysLeft <= 3;
    const expiryOuter = urgent ? "bg-amber-50 border-t border-amber-100" : "bg-neutral-50 border-t border-neutral-100";
    const expiryText = urgent ? "text-amber-700" : "text-neutral-500";
    const pillCls = urgent ? "bg-[#FDE68A] text-amber-800" : "bg-neutral-200 text-neutral-600";

    return (
        <div className="w-full bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden text-left transition-shadow duration-200 hover:shadow-md hover:border-neutral-300">
            <div className="flex items-center gap-4 sm:gap-5 px-5 pt-5 pb-4 sm:px-7 sm:pt-7 sm:pb-6">
                <WelcomeCompanyAvatar logoUrl={logoUrl} companyName={companyName} isLoading={isLoading} />
                <div className="min-w-0">
                    {isLoading ? (
                        <>
                            <div className="h-5 sm:h-6 w-48 bg-neutral-100 rounded animate-pulse mb-2" />
                            <div className="h-4 w-32 bg-neutral-100 rounded animate-pulse" />
                        </>
                    ) : (
                        <>
                            <p className="text-base sm:text-xl font-semibold text-gray-900 leading-snug">{jobTitle}</p>
                            <p className="text-sm sm:text-base text-neutral-500 mt-1">{companyDisplay}</p>
                        </>
                    )}
                </div>
            </div>

            <div className="border-t border-neutral-100 px-5 py-3 sm:px-7 sm:py-4 grid grid-cols-[auto_1fr] gap-x-5 sm:gap-x-6 gap-y-2 sm:gap-y-2.5">
                <span className="text-xs sm:text-sm text-neutral-400 font-medium self-center">Department</span>
                <span className="text-xs sm:text-sm text-neutral-600 font-medium">{isLoading ? "—" : department}</span>
                <span className="text-xs sm:text-sm text-neutral-400 font-medium self-center">Job ID</span>
                <span className="text-xs sm:text-sm text-neutral-600 font-medium truncate">{isLoading ? "—" : jobId}</span>
                <span className="text-xs sm:text-sm text-neutral-400 font-medium">Invited Date</span>
                <span className="text-xs sm:text-sm text-neutral-600 font-medium">{invitedDate || "—"}</span>
            </div>

            <div className={`flex items-center justify-center gap-2 px-5 py-3 sm:px-7 sm:py-3.5 ${expiryOuter}`}>
                <ClockIcon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${expiryText}`} />
                <span className={`text-xs sm:text-sm font-medium ${expiryText}`}>Expires {expiryDate}</span>
                <span className={`text-xs sm:text-sm font-semibold px-2 sm:px-2.5 py-0.5 rounded-full ${pillCls}`}>{daysLeft}d left</span>
            </div>
        </div>
    );
}

export default function ApplyNowV2() {
    const { job_id } = useParams();
    const queryParams = useQuery();
    const payloadToken = (() => {
        try {
            const token = queryParams.get("token");
            return token ? JSON.parse(atob(token)) : null;
        } catch {
            return null;
        }
    })();
    const [currentStep, setCurrentStep] = useState(2);
    const [jobDetail, setJobDetail] = useState<Job | undefined>(undefined);
    const [isLoadingJobDetail, setIsLoadingJobDetail] = useState(true);
    const [isJobNotFound, setIsJobNotFound] = useState(false);
    const [isStartInterview, setIsStartInterview] = useState<boolean>(false);
    const [isLoadingStartInterview, setIsLoadingStartInterview] = useState<boolean>(false);
    const [cvData, setCvData] = useState<CVDataInfo | null>(null);
    const [surveyCompleted, setSurveyCompleted] = useState(false);
    const [isLoadingCV, setLoadingCV] = useState<boolean>(true);
    const [isLoadingProcessInterview, setLoadingProcessInterview] = useState<boolean>(false);
    const [interviewCompleted, setInterviewCompleted] = useState<boolean>(false);
    const [isLoadingAssessment, setIsLoadingAssessment] = useState<boolean>(false);
    const [isLoadingAIInterview, setIsLoadingAIInterview] = useState<boolean>(false);
    const [isLoadingCompanyInfo, setIsLoadingCompanyInfo] = useState<boolean>(false);
    // True when the company-info fetch (which carries the interview_engine) failed.
    // We must NOT default to the v1 engine on failure — an ElevenLabs company would
    // be routed into the wrong flow. Show a retry instead.
    const [companyInfoLoadFailed, setCompanyInfoLoadFailed] = useState<boolean>(false);
    const [currentInterviewStatus, setCurrentInterviewStatus] = useState<string>("");
    // Mirror of currentInterviewStatus that updates synchronously — the v2 engine
    // calls onInterviewStatusChange then onCloseModal back-to-back, so the state
    // update hasn't flushed yet when handleCheckInterviewScore reads it. The ref
    // lets that handler decide reliably whether the run was too short.
    const interviewStatusRef = useRef<string>("");
    const [isLoadingInterviewStatus, setIsLoadingInterviewStatus] = useState<boolean>(false);
    // Pre-load of getInterviewStatusV2 on page mount (default true so we
    // never flash the welcome screen before the status comes back).
    const [isPreloadingInterviewStatus, setIsPreloadingInterviewStatus] = useState<boolean>(true);
    const [isLoadingApplicantStatus, setIsLoadingApplicantStatus] = useState<boolean>(true);
    const [isAssessmentFormCompleted, setIsAssessmentFormCompleted] = useState<boolean>(false);
    const [assessment, setAssessment] = useState<FormAssessment | null>(null);
    const [interviewAssessment, setInterviewAssessment] = useState<InterviewAssessment | null>(null);
    const [formattedSections, setFormattedSections] = useState<CustomInterviewSection[]>([]);
    const [companyInfo, setCompanyInfo] = useState<Company | undefined>(undefined);
    const [isRequestingNewLink, setIsRequestingNewLink] = useState<boolean>(false);
    const [isRequestedNewLink, setIsRequestedNewLink] = useState<boolean>(false);
    const [currentTipIndex, setCurrentTipIndex] = useState<number>(0);
    const [applicantStatus, setApplicantStatus] = useState<string>("");
    // Backend funnel stage for this applicant on this job — separate from
    // `applicantStatus` and shipped on the same endpoint. Stored here for
    // downstream gating; no consumer yet.
    const [funnelStatus, setFunnelStatus] = useState<string>("");
    const [applicantAppliedDate, setApplicantAppliedDate] = useState<string>("");
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [applicationId, setApplicationId] = useState<string>("");
    const [applicantId, setApplicantId] = useState<string>("");
    const [assessmentInfoError, setAssessmentInfoError] = useState<string>("");
    const [isAssessmentLinkValid, setIsAssessmentLinkValid] = useState<boolean | null>(null);
    const [isAssessmentResent, setIsAssessmentResent] = useState<boolean>(false);
    const [isWorkspaceGraceExpired, setIsWorkspaceGraceExpired] = useState<boolean>(false);
    const [assessmentInfoStatus, setAssessmentInfoStatus] = useState<string>("");
    // #3328 (BE ourteam-backend#4128): the application already has stored
    // eligibility answers — skip the Eligibility Check step instead of asking
    // again (a re-submit would overwrite them). Explicit === true; absent on
    // an older BE ⇒ false ⇒ today's behavior.
    const [eligibilitySubmitted, setEligibilitySubmitted] = useState<boolean>(false);

    // Customizable Hiring Flow runner (#3077): for custom-flow jobs the SERVER
    // drives the step order via GET /jobs/applications/{id}/pending-assessments.
    // `flowInfo` is null / carries no step_ids for default jobs — the legacy
    // hardcoded machine (interview → form → eligibility) then runs untouched.
    const [flowInfo, setFlowInfo] = useState<PendingAssessmentsResponse | null>(null);
    // Sticky: once a job is detected as flow-managed it stays so, even after the
    // last step completes and the pending list empties (→ completed card).
    const [flowEverManaged, setFlowEverManaged] = useState<boolean>(false);
    const [isConfirmingFlowStep, setIsConfirmingFlowStep] = useState<boolean>(false);
    const [uploadingFlowItem, setUploadingFlowItem] = useState<string>("");
    const [flowActionError, setFlowActionError] = useState<string>("");

    const callObject = useCallObject({}) as DailyCall;

    const tips: string[] = [
        "Almost there — getting your insights ready!",
        "Listening closely... even to the pauses.",
        "Analyzing your answers frame by frame.",
        "Spotting strengths and growth areas.",
        "Running the final checks on your delivery."
    ];

    const getCandidateName = () => {
        if (!cvData) return "Anonymous Candidate";
        const cv = cvData
        return cv?.CV_data?.name || "Anonymous Candidate";
    };

    const INTERVIEW_CONFIG = {
        conversationName: `${getCandidateName()}: Screening Interview (${new Date().toISOString()})`,
        conversationalContext: `Never ask the user about a 'position', this is a general screening interview. \n\nCandidate CV/Resume:\n${formatCV(cvData?.CV_data || null)}`,
    };

    const REPLICA_CONFIG = {
        label: import.meta.env.VITE_TAVUS_REPLICA_LABEL,
        replicaId: import.meta.env.VITE_TAVUS_REPLICA_ID,
        personaId: import.meta.env.VITE_TAVUS_PERSONA_ID,
        greeting: import.meta.env.VITE_TAVUS_GREETING,
    };

    const fetchAssessmentInfo = async (): Promise<boolean> => {
        if (!payloadToken?.user_id || !job_id) return false;
        try {
            const resp = await getAssessmentInfo({
                applicant_id: payloadToken.user_id,
                job_id,
                // Forward the link's embedded access_token so the BE can tell
                // whether the candidate is on the latest resent link or a stale
                // one. The link's `token` query param is already decoded into
                // payloadToken above, which carries this JWT. Undefined when the
                // link predates this field — BE then falls back to old behavior.
                access_token: payloadToken.access_token,
            });
            if (resp.data?.data?.is_assessment_link_valid !== undefined) {
                setIsAssessmentLinkValid(resp.data.data.is_assessment_link_valid);
            }
            if (resp.data?.data?.is_assessment_resent !== undefined) {
                setIsAssessmentResent(resp.data.data.is_assessment_resent);
            }
            if (resp.data?.data?.is_workspace_grace_expired !== undefined) {
                setIsWorkspaceGraceExpired(resp.data.data.is_workspace_grace_expired);
            }
            if (resp.data?.data?.status !== undefined) {
                setAssessmentInfoStatus(resp.data.data.status);
            }
            // Derived from EVERY response (not only the true case) so a
            // refetch that reports no stored answers clears a stale skip.
            setEligibilitySubmitted(resp.data?.data?.eligibility_submitted === true);
            if (resp.data?.data?.access_token) {
                setCookie("tempAuthToken", resp.data.data.access_token);
                return true;
            }
            return false;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setAssessmentInfoError(error.response?.data?.message || "Failed to load screening info");
            }
            catchErrorLog(`Error fetching assessment info: ${error}`);
            return false;
        }
    };


    useEffect(() => {
        if (currentStep === 2 && isLoadingProcessInterview) {
            // Start a timer to change the tip every 5 seconds
            let interval: NodeJS.Timeout | undefined;
            if (currentTipIndex < tips.length - 1) {
                interval = setInterval(() => {
                    setCurrentTipIndex((prevIndex) => {
                        if (prevIndex < tips.length - 1) {
                            return prevIndex + 1;
                        } else {
                            return 0;
                        }
                    });
                }, 3000);
            }

            return () => clearInterval(interval);
        }
    }, [interviewAssessment, isLoadingProcessInterview, currentStep]);

    useEffect(() => {
        if (currentStep === 5) {
            onEndAssessment();
            removeCookie("tempAuthToken");
        }
    }, [currentStep])

    useEffect(() => {
        if (currentStep !== 3 || !assessment || isAssessmentFormCompleted || !job_id) return;
        recordFormAssessmentStarted(job_id).catch((error) => {
            catchErrorLog(`Error recording form assessment started: ${error}`);
        });
    }, [currentStep, assessment, isAssessmentFormCompleted, job_id]);

    useEffect(() => {
        if (currentStep !== 4 || !jobDetail?.is_enable_work_authorization || !job_id) return;
        recordEligibilityCheckStarted(job_id).catch((error) => {
            catchErrorLog(`Error recording eligibility check started: ${error}`);
        });
    }, [currentStep, jobDetail?.is_enable_work_authorization, job_id]);

    // Heartbeat: periodically ping server while assessment is in progress
    // Server detects drop-off when heartbeat stops
    useEffect(() => {
        if (!isStartInterview || currentStep >= 5) return;

        const sendHeartbeat = () => {
            const authToken = getCookie("tempAuthToken") || getCookie("authToken");
            const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

            fetch(`${apiURL}/assessments/heartbeat`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${authToken}`,
                },
                body: JSON.stringify({ job_id }),
            }).catch(() => {});
        };

        // Send initial heartbeat immediately
        sendHeartbeat();

        // Send heartbeat every 30 seconds (testing)
        const interval = setInterval(sendHeartbeat, 30000);

        return () => clearInterval(interval);
    }, [isStartInterview, currentStep]);

    const fetchCV = async () => {
        try {
            setLoadingCV(true);
            const response = await getMyCV();
            if (response.data) {
                // Add verification that data is not empty
                const hasValidData =
                    response.data &&
                    typeof response.data === "object" &&
                    Object.keys(response.data).length > 0;

                if (hasValidData) {
                    setCvData(response.data);
                } else {
                    setCvData(null);
                }
            } else {
                setCvData(null);
            }
        } catch (error) {
            setCvData(null);
            catchErrorLog(`Error fetching CV: ${error}`);
        } finally {
            setLoadingCV(false);
        }
    };

    const fetchJobDetail = async () => {
        if (!job_id) {
            catchErrorLog("No job ID provided in URL");
            setIsLoadingJobDetail(false);
            return;
        }

        try {
            setIsLoadingJobDetail(true);

            const data = await getJobDetail(job_id);
            setJobDetail(data);

            getJobApplicantStatus(job_id).then((resp) => {
                if (resp.status === 200) {
                    setApplicantStatus(resp.data.data.status)
                    setApplicantAppliedDate(resp.data.data.applied_date)
                    if (resp.data.data.funnel_status) {
                        setFunnelStatus(resp.data.data.funnel_status)
                    }
                    if (resp.data.data.application_id) {
                        setApplicationId(resp.data.data.application_id)
                    }
                    if (resp.data.data.applicant_id) {
                        setApplicantId(resp.data.data.applicant_id)
                    }
                }
            }).finally(() => {
                setIsLoadingApplicantStatus(false);
            })
        } catch (err: any) {
            if (err?.response?.status === 404) {
                setIsJobNotFound(true);
            }
            setIsLoadingApplicantStatus(false);
            catchErrorLog(`Error fetching job details: ${err}`);
        } finally {
            setIsLoadingJobDetail(false);
        }
    };

    useEffect(() => {
        if (!payloadToken) {
            setIsLoadingJobDetail(false);
            setIsLoadingApplicantStatus(false);
            setLoadingCV(false);
            return;
        }
        fetchAssessmentInfo().then((success) => {
            if (success) {
                fetchCV();
                fetchJobDetail();
            } else {
                setIsLoadingJobDetail(false);
                setIsLoadingApplicantStatus(false);
                setLoadingCV(false);
            }
        });
    }, []);

    // Pre-load assessment data for the welcome screen so durations are accurate
    useEffect(() => {
        if (!jobDetail?.assessment_ids?.length) return;

        const interviewId = jobDetail.assessment_ids.find(a => a.type === "Interview")?.id;
        if (interviewId) {
            getAssessment(interviewId).then(data => {
                if (data) setInterviewAssessment(data as InterviewAssessment);
            });
        }

        const formId = jobDetail.assessment_ids.find(a => a.type === "Form")?.id;
        if (formId) {
            getAssessment(formId).then(data => {
                if (data) setAssessment(data as FormAssessment);
            });
        }
    }, [jobDetail?.id]);

    // Flag-driven step order (see hiringFlowScreenOrder above). Declared before
    // the effects below so they can pick the flow's entry screen; whether it
    // steers the machine at all is decided by `useFlagFlowOrder` further down
    // (the server-driven runner (#3077) keeps precedence whenever active).
    const flagFlowOrder = hiringFlowScreenOrder(jobDetail);

    // Pre-load interview status before the user clicks "Begin Screening".
    // Fetched here so the welcome flow can immediately route to
    // InterviewShortCard / completed-state without waiting for the
    // post-click `startInterview` round-trip.
    //
    // Gated on applicantStatus being loaded AND valid (not Archived/Rejected)
    // so we skip the call for invalid applicants — interview status is
    // irrelevant once the application itself is dead.
    //
    // Manages `isPreloadingInterviewStatus` so downstream render gates know
    // when it's safe to show the welcome page. Default state is `true`, and
    // we flip to `false` when the pre-load completes OR when we determine
    // pre-load is not applicable (no Interview assessment, invalid applicant,
    // screening-only job).
    useEffect(() => {
        if (isLoadingJobDetail || isLoadingApplicantStatus) return;

        const hasInterviewAssessment = !!jobDetail?.assessment_ids?.find(a => a.type === "Interview");
        if (!hasInterviewAssessment) {
            setIsPreloadingInterviewStatus(false);
            return;
        }
        if (applicantStatus === "Archived" || applicantStatus === "Rejected") {
            setIsPreloadingInterviewStatus(false);
            return;
        }
        if (!interviewAssessment?.id) return;

        setIsPreloadingInterviewStatus(true);
        getInterviewStatusV2(interviewAssessment.id).then((res) => {
            const status = res?.data?.status || "";
            if (!status) return;
            // Intentionally skip promoting Aborted/Partial into state — the
            // InterviewShortCard render path now relies solely on the live
            // `onInterviewStatusChange` callback from the interview component,
            // so a page reload after a previous too-short session doesn't
            // surface the retake card out of context.
            if (status === interviewStatusAborted || status === interviewStatusPartial) {
                return;
            }
            setCurrentInterviewStatus(status);
            if (status === interviewStatusComplete) {
                setInterviewCompleted(true);
                // Flow-managed jobs: the flow-sync effect owns currentStep —
                // don't jump to the hardcoded form step here (#3077).
                if (!flowInfo?.current_step_id) {
                    // Flag-order jobs enter at the flow's first screen and let
                    // the render-phase skips walk past completed steps — a
                    // hard jump to the form would skip a form-first flow.
                    setCurrentStep(flagFlowOrder.length > 0 ? flagFlowOrder[0] : 3);
                }
            }
        }).finally(() => {
            setIsPreloadingInterviewStatus(false);
        });
    }, [interviewAssessment?.id, isLoadingApplicantStatus, applicantStatus, isLoadingJobDetail, jobDetail]);

    // ---- Hiring-flow runner (#3077) ------------------------------------------
    // Monotonic guard so overlapping flow reads (mount + post-action) can't land
    // out of order — a stale snapshot must not revert the candidate to an older
    // step or hide the completion state.
    const flowRequestRef = useRef(0);
    // #3463 / BE #4512: gates are judged inside the form-submit request, but the
    // /assessments/submit response carries the SUBMISSION row, not the application
    // — so unlike eligibility (whose /jobs/apply response carries the post-gate
    // status) a form knockout has no in-band signal. Re-pull the applicant status
    // after a form submit: this endpoint needs only the job id (no application_id,
    // see BE #4511), and toPauseState(applicantStatus) renders the paused screen.
    const refreshApplicantStatus = useCallback(() => {
        if (!job_id) return;
        getJobApplicantStatus(job_id).then((resp) => {
            if (resp.status === 200 && resp.data?.data?.status) {
                setApplicantStatus(resp.data.data.status);
                if (resp.data.data.funnel_status) {
                    setFunnelStatus(resp.data.data.funnel_status);
                }
            }
        }).catch(() => { /* best-effort — the load-time status stands */ });
    }, [job_id]);

    const refreshFlow = useCallback(async (appId?: string) => {
        const id = appId || applicationId;
        if (!id) return;
        const requestId = ++flowRequestRef.current;
        try {
            const data = await getPendingAssessments(id);
            if (requestId !== flowRequestRef.current) return;
            setFlowInfo(data);
        } catch (error) {
            if (requestId !== flowRequestRef.current) return;
            catchErrorLog(`Error fetching pending flow steps: ${error}`);
        }
    }, [applicationId]);

    useEffect(() => {
        if (applicationId) refreshFlow(applicationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationId]);

    const flowDetected = !!(flowInfo && (flowInfo.current_step_id || (flowInfo.steps || []).some((s) => !!s.step_id)));
    useEffect(() => {
        if (flowDetected) setFlowEverManaged(true);
    }, [flowDetected]);
    const isFlowManaged = flowDetected || flowEverManaged;
    // The server list holds only the REMAINING steps; the current one is marked
    // is_current (falls back to the first remaining while the flow hasn't started).
    const flowCurrent: PendingFlowStep | undefined = isFlowManaged && flowInfo
        ? (flowInfo.steps.find((s) => s.is_current) || flowInfo.steps.find((s) => !!s.step_id))
        : undefined;
    // #3463 (BE #4505): a gate paused the flow at the current step pending an HR
    // resolution — "knockout_flagged" (an answer matched a knockout condition) or
    // "deadline_missed" (the step's time window ended). Gates flag & pause, never
    // auto-reject, so the candidate sees a neutral paused screen: no step screen,
    // and NEVER the completion card.
    //
    // Three detection sources, most-immediate first — all derive from the same
    // application status the BE gate engine writes:
    // 1. submitPausedState — the /jobs/apply response the candidate JUST received
    //    carries the flagged status; react instantly, no second round-trip.
    // 2. pending-assessments flow_state (when the refetch lands).
    // 3. applicantStatus from GET /jobs/{job_id}/applicant/status at page load —
    //    this makes a reload land on the paused screen even though that response
    //    carries no application_id today (so pending-assessments is never called
    //    on load — BE gap, ticketed separately).
    const toPauseState = (s?: string | null): "knockout_flagged" | "deadline_missed" | null =>
        s === "Knockout Flagged" || s === "knockout_flagged" ? "knockout_flagged"
            : s === "Deadline Missed" || s === "deadline_missed" ? "deadline_missed"
                : null;
    const [submitPausedState, setSubmitPausedState] = useState<"knockout_flagged" | "deadline_missed" | null>(null);
    const flowPauseState = submitPausedState
        || toPauseState(flowInfo?.flow_state)
        || toPauseState(applicantStatus);
    const isFlowKnockoutFlagged = flowPauseState === "knockout_flagged";
    const isFlowDeadlineMissed = flowPauseState === "deadline_missed";
    const isFlowPaused = isFlowKnockoutFlagged || isFlowDeadlineMissed;

    // Map the server's current step onto the legacy screen numbers so the
    // existing interview/form/eligibility screens render unchanged. No current
    // step left ⇒ the flow is complete (step 5 card).
    useEffect(() => {
        if (!isFlowManaged || !flowInfo) return;
        // Paused flow: keep whatever step is current — the render guard shows the
        // paused screen; advancing to the step-5 card here would flash
        // "Screening Completed!" for a flow that is NOT complete.
        if (isFlowPaused) return;
        if (!flowCurrent) {
            setCurrentStep(5);
            return;
        }
        const stepNumberByType: Record<string, number> = { ai_interview: 2, form_assessment: 3, eligibility_check: 4 };
        const n = stepNumberByType[flowCurrent.type];
        if (n) setCurrentStep(n);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFlowManaged, flowInfo, flowCurrent?.step_id, flowCurrent?.type, isFlowPaused]);

    // A custom flow can hold multiple interviews/forms — load the CURRENT step's
    // assessment when it differs from what the legacy job-level loaders mounted.
    // Guarded by a monotonic request id: if the server advances to another step
    // while a load is in flight, the stale response is discarded so it can't
    // overwrite the current step's assessment.
    const flowAssessmentRequestRef = useRef(0);
    useEffect(() => {
        if (!isFlowManaged || !flowCurrent?.assessment_id) return;
        const requestId = ++flowAssessmentRequestRef.current;
        if (flowCurrent.type === "ai_interview" && interviewAssessment?.id !== flowCurrent.assessment_id) {
            setInterviewCompleted(false);
            interviewStatusRef.current = "";
            setCurrentInterviewStatus("");
            getAssessment(flowCurrent.assessment_id).then((data) => {
                if (!data || requestId !== flowAssessmentRequestRef.current) return;
                const interviewData = data as InterviewAssessment;
                setInterviewAssessment(interviewData);
                setFormattedSections(interviewData.content.sections.map(section => ({
                    title: section.title,
                    instruction: section.instruction,
                    duration: section.duration,
                    questions: (section.questions || []),
                    transitions: section.transition ? { before: section.transition } : undefined
                })));
            });
        }
        if (flowCurrent.type === "form_assessment" && assessment?.id !== flowCurrent.assessment_id) {
            setIsAssessmentFormCompleted(false);
            setSurveyCompleted(false);
            getAssessment(flowCurrent.assessment_id).then((data) => {
                if (data && requestId === flowAssessmentRequestRef.current) setAssessment(data as FormAssessment);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFlowManaged, flowCurrent?.step_id, interviewAssessment?.id, assessment?.id]);

    // After a completion signal, poll briefly until the server advances the
    // flow (the BE advances inside the completion request itself, so the first
    // refetch usually already carries the next step).
    useEffect(() => {
        if (!isFlowManaged || !flowCurrent) return;
        // Paused by a gate (#3463): the server will not advance until HR
        // resolves — stop polling, the paused screen is already showing.
        if (isFlowPaused) return;
        const waitingOnAdvance =
            (flowCurrent.type === "ai_interview" && interviewCompleted) ||
            (flowCurrent.type === "form_assessment" && (surveyCompleted || isAssessmentFormCompleted));
        if (!waitingOnAdvance) return;
        let tries = 0;
        const interval = setInterval(() => {
            tries += 1;
            refreshFlow();
            if (tries >= 5) clearInterval(interval);
        }, 2000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFlowManaged, flowCurrent?.step_id, interviewCompleted, surveyCompleted, isAssessmentFormCompleted, isFlowPaused]);
    // --------------------------------------------------------------------------

    // ---- Flag-driven step order ----------------------------------------------
    // company_hiring_flow_enabled === true ⇒ the legacy machine follows the
    // job's hiring_flow order (flagFlowOrder, declared above the pre-load
    // effect). Never active while the server-driven runner (#3077) is.
    const useFlagFlowOrder = !isFlowManaged && flagFlowOrder.length > 0;

    // currentStep initializes to 2 (AI interview) before the job detail
    // arrives — once the flag order is known, enter at ITS first screen.
    const flagFlowEntrySyncedRef = useRef(false);
    useEffect(() => {
        if (!useFlagFlowOrder || flagFlowEntrySyncedRef.current) return;
        flagFlowEntrySyncedRef.current = true;
        if (currentStep === 2 && flagFlowOrder[0] !== 2) {
            setCurrentStep(flagFlowOrder[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useFlagFlowOrder]);

    // What follows the AI interview drives the engines' "next up" messaging —
    // under a flag-driven order only the steps AFTER the interview count.
    // Legacy order keeps today's values exactly.
    const hasEligibilityMetadata = !!(
        jobDetail?.work_authorization_metadata?.collect_employment_status ||
        jobDetail?.work_authorization_metadata?.collect_expected_salary ||
        jobDetail?.work_authorization_metadata?.collect_start_availability ||
        jobDetail?.work_authorization_metadata?.collect_current_salary ||
        jobDetail?.work_authorization_metadata?.collect_relocation_willingness ||
        jobDetail?.work_authorization_metadata?.collect_weekend_shift_willingness
    );
    const screensAfterInterview = useFlagFlowOrder ? flagFlowOrder.slice(flagFlowOrder.indexOf(2) + 1) : [3, 4];
    const hasFormAfterInterview = !!assessment && screensAfterInterview.includes(3);
    const hasEligibilityAfterInterview = hasEligibilityMetadata && screensAfterInterview.includes(4);
    // --------------------------------------------------------------------------

    const handleCheckInterviewScore = useCallback(async () => {
        // ALLOWLIST gate: ONLY a completed interview may advance to the next
        // step. The engines call onCloseModal on EVERY end — including
        // pre-start exits like the waiting room's "Request Submitted" → Close
        // (issue-report/retake flow), where no status was ever reported. The
        // old guard only blocked Aborted/Partial, so an empty/unknown status
        // slipped through and let a candidate reach the Form / Eligibility
        // Check steps without doing the interview at all. Requiring Complete
        // also removes the dependency on onInterviewStatusChange firing
        // BEFORE onCloseModal (callback-ordering race): any path that never
        // reported Complete simply stays on the interview step (Aborted/
        // Partial keep showing the InterviewShortCard retake, as before).
        // Read the ref, not the state, since the status was just set
        // synchronously right before this call and the state may not have
        // flushed.
        const finalStatus = interviewStatusRef.current || currentInterviewStatus;
        if (finalStatus !== interviewStatusComplete) {
            return;
        }

        trackEvent('ai_interview_completed', {
            job_id: jobDetail?.id || "",
        })

        setLoadingProcessInterview(true);
        if (!interviewAssessment) {
            getVideo(undefined, "First Time Interview", payloadToken.user_id)
                .then((resp) => {
                    if (resp) {
                        handleNextStep();
                    }
                }).finally(() => {
                    setLoadingProcessInterview(false);
                })
            return;
        }

        setLoadingProcessInterview(false);
        handleNextStep();
    }, [currentInterviewStatus, interviewAssessment, jobDetail, payloadToken?.user_id]);

    const handleNextStep = () => {
        // Flow-managed jobs (#3077): the server owns the order — refetch the
        // remaining steps instead of incrementing the hardcoded machine. The
        // delayed refetch absorbs the BE advance that runs inside the
        // completion request we just fired.
        if (isFlowManaged) {
            refreshFlow();
            setTimeout(() => { refreshFlow(); }, 1500);
            return;
        }
        if (useFlagFlowOrder) {
            // The job's hiring_flow owns the order — move to the screen after
            // the current one, or complete when it was the last flow step.
            setCurrentStep((prev) => {
                const idx = flagFlowOrder.indexOf(prev);
                return idx !== -1 && idx < flagFlowOrder.length - 1 ? flagFlowOrder[idx + 1] : 5;
            });
            return;
        }
        setCurrentStep((prev) => Math.min(prev + 1, 5));
    };

    // #3328: auto-advance past an already-submitted Eligibility Check. Lives in
    // an effect, not the render branch — a render-time setState can warn and,
    // under Strict Mode's double render, advance twice.
    useEffect(() => {
        if (currentStep === 4 && !isFlowManaged && eligibilitySubmitted) {
            handleNextStep();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, isFlowManaged, eligibilitySubmitted]);

    const getApplicantSttaus = (status: string): string => {
        let applicantStatus: string = "";
        if (status === interviewStatusComplete) {
            applicantStatus = "Pending Review";
        } else if (status === interviewStatusPartial) {
            applicantStatus = "Partial Interview";
        } else if (status === interviewStatusAborted) {
            applicantStatus = "Aborted Interview";
        } else {
            applicantStatus = "Interview Incomplete";
        }

        return applicantStatus;
    }

    const handlePrevStep = () => {
        if (useFlagFlowOrder) {
            // Back follows the flow order; the first flow step has nowhere to
            // go back to, so it stays put.
            setCurrentStep((prev) => {
                const idx = flagFlowOrder.indexOf(prev);
                return idx > 0 ? flagFlowOrder[idx - 1] : prev;
            });
            return;
        }
        if (currentStep === 3 && !assessment) {
            setCurrentStep(2); // If no assessment and at interview step, go back to resume upload
        } else {
            setCurrentStep((prev) => Math.max(prev - 1, 1));
        }
    };

    const onEndAssessment = useCallback(async () => {
        if (!jobDetail) return;
        const applicantStatus: string = getApplicantSttaus(currentInterviewStatus);
        await updateApplicantStatusAssessmentCompletion(payloadToken.job_id, payloadToken.user_id, applicantStatus);
        console.log(`Interview completed and applicant status updated into ${applicantStatus}.`);
    }, [currentInterviewStatus, payloadToken, jobDetail]);

    const onCompleteInterview = useCallback(async () => {
        if (assessment || (jobDetail && jobDetail.is_enable_work_authorization)) return
        const applicantStatus: string = getApplicantSttaus("Complete");
        await updateApplicantStatusAssessmentCompletion(payloadToken.job_id, payloadToken.user_id, applicantStatus);
        console.log(`Interview completed and applicant status updated into ${applicantStatus}.`);
    }, [payloadToken, assessment, jobDetail]);

    const onRequestNewLink = () => {
        setIsRequestingNewLink(true);
        applicantRequestNewAssessmentLink(payloadToken.job_id, payloadToken.user_id).then((resp) => {
            if (resp.status === 200) {
                setIsRequestedNewLink(true);
            }
        }).finally(() => {
            setIsRequestingNewLink(false);
        });
    }

    const onRetakeInterviewTooShort = async () => {
        if (!interviewAssessment?.id) return;

        // Re-fetch the latest interview status before issuing the retake.
        // Guards against a stale UI where the interview was completed in
        // another tab/session after this page's pre-load fired — in that
        // case we must NOT reset the status back to "Pending Interview".
        try {
            const statusRes = await getInterviewStatusV2(interviewAssessment.id);
            const latestStatus = statusRes?.data?.status || "";
            if (latestStatus === interviewStatusComplete) {
                setCurrentInterviewStatus(interviewStatusComplete);
                setInterviewCompleted(true);
                return;
            }
        } catch (err) {
            catchErrorLog(`onRetakeInterviewTooShort: pre-check failed: ${err}`);
            // Fall through and attempt the retake anyway — failing the
            // pre-check shouldn't block legitimate retakes.
        }

        updateInterviewStatusV2({
            new_status: "Pending Interview",
        }).then((resp) => {
            if (resp) {
                setCurrentInterviewStatus("Pending Interview");
            }
        });
    }

    const startInterview = useCallback(async () => {
        if (!jobDetail) return;

        applicantStartInterview(payloadToken.job_id, payloadToken.user_id).catch((error) => {
            catchErrorLog(`Error starting interview: ${error}`);
        })

        setIsLoadingStartInterview(true);
        setIsLoadingCompanyInfo(true);
        setCompanyInfoLoadFailed(false);
        try {
            const respCompany = await getCompanyInfo(jobDetail.company_id);
            if (respCompany.status === 200) {
                setCompanyInfo(respCompany.data.data);
            } else {
                // Don't fall through to v1 — we don't know the engine. Surface a retry.
                setCompanyInfoLoadFailed(true);
            }
        } catch {
            setCompanyInfoLoadFailed(true);
        }
        setIsLoadingCompanyInfo(false);

        if (jobDetail.assessment_ids && jobDetail.assessment_ids.length > 0) {
            const assessmentFormID = jobDetail.assessment_ids.find(a => a.type === "Form")?.id;
            if (assessmentFormID) {
                setIsLoadingAssessment(true);
                const assessmentFormData = await getAssessment(assessmentFormID || "");
                if (assessmentFormData) {
                    setAssessment(assessmentFormData as FormAssessment);
                }

                const assessmentFormStatus = await getAssessmentFormStatus(assessmentFormID, jobDetail.id);
                if (assessmentFormStatus && assessmentFormStatus.status === 200) {
                    setIsAssessmentFormCompleted(assessmentFormStatus.data.data.is_complete);
                    if (assessmentFormStatus.data.data.is_complete) {
                        setSurveyCompleted(true);
                    }
                }

                setIsLoadingAssessment(false);
            }

            const assessmentInterviewID = jobDetail.assessment_ids.find(a => a.type === "Interview")?.id;
            if (assessmentInterviewID) {
                setIsLoadingAIInterview(true);
                const assessmentInterviewData = await getAssessment(assessmentInterviewID || "");
                if (assessmentInterviewData) {
                    const interviewData = assessmentInterviewData as InterviewAssessment;
                    setInterviewAssessment(interviewData);

                    // Format sections for the CustomVoiceInterviewV2 component
                    const sections = interviewData.content.sections.map(section => ({
                        title: section.title,
                        instruction: section.instruction,
                        duration: section.duration,
                        questions: (section.questions || []),
                        transitions: section.transition ? { before: section.transition } : undefined
                    }));
                    setFormattedSections(sections);

                    // Interview status is pre-loaded by the page-mount
                    // useEffect on interviewAssessment.id, so we skip the
                    // duplicate getInterviewStatusV2 call here.
                }

                setIsLoadingAIInterview(false);
                setIsLoadingStartInterview(false);
                setIsStartInterview(true);
                return;
            }
        }

        try {
            setIsLoadingInterviewStatus(true);
            const res = await getInterviewStatusV2(null);
            setCurrentInterviewStatus(res?.data?.status || "");
            if (res?.data?.status === "Complete") {
                setInterviewCompleted(true);
                if (!isFlowManaged) {
                    // Same flag-order entry rule as the pre-load effect above.
                    setCurrentStep(flagFlowOrder.length > 0 ? flagFlowOrder[0] : 3);
                }
            }
        } finally {
            setIsLoadingAssessment(false);
            setIsLoadingAIInterview(false);
            setIsLoadingInterviewStatus(false);
            setIsLoadingStartInterview(false);
            setIsStartInterview(true);
        }
    }, [jobDetail, payloadToken?.job_id, payloadToken?.user_id]);

    // ---- Hiring-flow step renderers (#3077) ----------------------------------
    const flowCardShadow = { boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)' };

    const renderFlowProcessingCard = () => (
        <div className="mx-auto sm:w-[550px] h-full justify-center align-middle items-center flex">
            <div className="p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6" style={flowCardShadow}>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900">Preparing your next step…</h2>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600" />
            </div>
        </div>
    );

    const renderExternalLinkStep = (step: PendingFlowStep) => (
        <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
            <div className="m-4 sm:m-0 w-full">
                <div className="p-6 sm:p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-5 w-full text-center" style={flowCardShadow}>
                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto">
                        <ExternalLinkIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold">{step.label || "External Link"}</h1>
                    <p className="text-gray-600">
                        Open the link below and complete it. When you're done, come back here and confirm to continue.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <Button
                            variant="outline"
                            className="border-blue-200 text-blue-600 hover:bg-blue-50"
                            onClick={() => step.url && window.open(step.url, "_blank", "noopener")}
                        >
                            <ExternalLinkIcon className="h-4 w-4" />
                            Open link
                        </Button>
                        <Button
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={isConfirmingFlowStep || !applicationId || !step.step_id}
                            onClick={async () => {
                                if (!applicationId || !step.step_id) return;
                                setFlowActionError("");
                                setIsConfirmingFlowStep(true);
                                try {
                                    await confirmFlowStep(applicationId, step.step_id);
                                    await refreshFlow();
                                } catch (error) {
                                    catchErrorLog(`Error confirming flow step: ${error}`);
                                    setFlowActionError("Something went wrong confirming this step. Please try again.");
                                } finally {
                                    setIsConfirmingFlowStep(false);
                                }
                            }}
                        >
                            {isConfirmingFlowStep ? (
                                <><Loader2Icon className="mr-2 h-4 w-4 animate-spin" />Confirming…</>
                            ) : (
                                <><CheckCircle2Icon className="h-4 w-4" />I've completed this step</>
                            )}
                        </Button>
                    </div>
                    {flowActionError && <p className="text-sm text-red-500">{flowActionError}</p>}
                </div>
            </div>
        </div>
    );

    const renderDocumentsStep = (step: PendingFlowStep) => {
        const items = Object.entries(step.items || {});
        return (
            <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                <div className="m-4 sm:m-0 w-full">
                    <div className="p-6 sm:p-8 flex flex-col rounded-3xl bg-white gap-5 w-full" style={flowCardShadow}>
                        <div className="text-center space-y-2">
                            <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto">
                                <UploadIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-bold">{step.label || "Document Collection"}</h1>
                            <p className="text-gray-600">Upload each required document below to continue.</p>
                        </div>
                        <div className="space-y-2">
                            {items.map(([name, state]) => (
                                <div key={name} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3">
                                    <span className="text-sm font-medium text-gray-900 truncate">{documentLabel(name)}</span>
                                    {state?.received ? (
                                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700">
                                            <CheckCircle2Icon className="w-4 h-4" />
                                            Received
                                        </span>
                                    ) : (
                                        <label className={`inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800 ${uploadingFlowItem ? "pointer-events-none opacity-60" : ""}`}>
                                            {uploadingFlowItem === name ? (
                                                <><Loader2Icon className="w-4 h-4 animate-spin" />Uploading…</>
                                            ) : (
                                                <><UploadIcon className="w-4 h-4" />Upload</>
                                            )}
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    e.target.value = "";
                                                    if (!file || !applicationId || !step.step_id) return;
                                                    setFlowActionError("");
                                                    setUploadingFlowItem(name);
                                                    try {
                                                        await uploadFlowDocument(applicationId, step.step_id, name, file);
                                                        await refreshFlow();
                                                    } catch (error) {
                                                        catchErrorLog(`Error uploading flow document: ${error}`);
                                                        setFlowActionError(`Failed to upload "${documentLabel(name)}". Please try again.`);
                                                    } finally {
                                                        setUploadingFlowItem("");
                                                    }
                                                }}
                                            />
                                        </label>
                                    )}
                                </div>
                            ))}
                        </div>
                        {flowActionError && <p className="text-sm text-red-500 text-center">{flowActionError}</p>}
                        <p className="text-xs text-gray-500 text-center">
                            Once every document is received, you'll automatically continue to the next step.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const renderHumanInterviewStep = (step: PendingFlowStep) => (
        <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
            <div className="m-4 sm:m-0 w-full">
                <div className="p-6 sm:p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-5 w-full text-center" style={flowCardShadow}>
                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto">
                        <HandshakeIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold">{step.label || "Interview"}</h1>
                    <p className="text-gray-600">
                        The hiring team will schedule this interview with you — check your email
                        (and WhatsApp, if you opted in) for the invitation and details.
                    </p>
                    <p className="text-sm text-gray-500">
                        There's nothing else to do here right now. You'll be notified when the next step is ready.
                    </p>
                </div>
            </div>
        </div>
    );
    // --------------------------------------------------------------------------

    const renderCurrentStep = () => {
        // Flow-managed jobs: the three NEW step types render dedicated screens;
        // interview/form/eligibility fall through to the legacy screens with
        // currentStep already synced by the flow effect (#3077).
        if (isFlowManaged && flowCurrent) {
            if (flowCurrent.type === "external_link") return renderExternalLinkStep(flowCurrent);
            if (flowCurrent.type === "documents") return renderDocumentsStep(flowCurrent);
            if (flowCurrent.type === "human_interview") return renderHumanInterviewStep(flowCurrent);
        }
        switch (currentStep) {
            case 2:
                // Aborted/partial → InterviewShortCard. Source of truth for
                // this status is the live `onInterviewStatusChange` callback
                // from the interview component (fires when the session ends
                // too short). Pre-loaded `getInterviewStatusV2` intentionally
                // does NOT promote Aborted/Partial into state — otherwise a
                // page reload after a previous too-short session would
                // surface this card out of context.
                if ([interviewStatusAborted, interviewStatusPartial].includes(currentInterviewStatus)) {
                    return (
                        <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                            <div className="m-4 sm:m-0">
                                <InterviewShortCard
                                    isApplyFlow
                                    description={(
                                        <>
                                            We didn’t receive enough responses to complete your screening.
                                            <br /><br />
                                            You may retake your interview now, or continue later while your screening link remains active.
                                        </>
                                    )}
                                    onRetakeInterview={onRetakeInterviewTooShort}
                                />
                            </div>
                        </div>
                    )
                }

                if (isLoadingProcessInterview) {
                    return (
                        <div className="mx-auto sm:w-[550px] h-full justify-center align-middle items-center flex">
                            <div className="p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <h2 className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">
                                    Reviewing Your AI Interview
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400 text-sm md:text-md">
                                    {tips[currentTipIndex]}
                                </p>
                                <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600`} />
                            </div>
                        </div>
                    );
                }

                if (interviewAssessment) {
                    if (interviewCompleted) {
                        // Flow mode: never advance from the render phase — the
                        // completion-polling effect refetches until the server
                        // moves the flow on (#3077).
                        if (isFlowManaged) {
                            return renderFlowProcessingCard();
                        }
                        handleNextStep();
                        return null;
                    }

                    return (
                        <div
                            className="container mx-auto px-4 sm:px-0 flex justify-center items-start sm:items-center max-w-6xl"
                            style={{ minHeight: "calc(100vh - 80px)" }}
                        >
                            <div className="w-full py-6 pb-20">
                            {jobDetail && cvData && interviewAssessment && (
                                <div className="sm:m-0">
                                    {/* Engine routing (#2810): wait for the company-info fetch to
                                        resolve before mounting EITHER engine, so an ElevenLabs
                                        company can't briefly mount the v1 Daily/pipecat flow during
                                        the async load. The fetch carries interview_engine — if it
                                        FAILS we must NOT default to v1 (an ElevenLabs company would
                                        be routed into the wrong, broken flow); show a retry instead.
                                        Only mount v1 once the company resolves to a non-EL engine. */}
                                    {isLoadingCompanyInfo ? null : companyInfoLoadFailed ? (
                                        <div className="w-full py-10 flex flex-col items-center justify-center gap-4 text-center">
                                            <p className="text-sm text-red-600 max-w-md">
                                                We couldn&apos;t load the interview configuration. Please refresh and try again.
                                            </p>
                                            <Button onClick={() => window.location.reload()}>Reload</Button>
                                        </div>
                                    ) : companyInfo?.interview_engine === "elevenlabs" ? (
                                        <ELVoiceInterview
                                            assessmentId={interviewAssessment.id as string}
                                            jobId={jobDetail.id}
                                            jobApplicationDetail={jobDetail}
                                            userId={payloadToken.user_id || ""}
                                            candidateName={cvData.CV_data.name || ""}
                                            interviewName={interviewAssessment.content.interviewName}
                                            description={interviewAssessment.content.description}
                                            aiName={interviewAssessment.content.aiName}
                                            language={interviewAssessment.content.interviewLanguage || "en-US"}
                                            voice={interviewAssessment.content.voice || "male"}
                                            sections={formattedSections.map((s) => ({ title: s.title, duration: s.duration }))}
                                            maxDuration={interviewAssessment.content.maxDuration || 20}
                                            onCompletedInterview={onCompleteInterview}
                                            onCloseModal={() => handleCheckInterviewScore()}
                                            onInterviewStatusChange={(status: string) => {
                                                interviewStatusRef.current = status;
                                                setCurrentInterviewStatus(status);
                                                if (status !== "Complete") {
                                                    let applicantStatus: string = "";
                                                    if (status === interviewStatusComplete) {
                                                        applicantStatus = "Pending Review";
                                                    } else if (status === interviewStatusPartial) {
                                                        applicantStatus = "Partial Interview";
                                                    } else if (status === interviewStatusAborted) {
                                                        applicantStatus = "Aborted Interview";
                                                    } else {
                                                        applicantStatus = "Interview Incomplete";
                                                    }
                                                    updateApplicantStatusAssessmentCompletion(
                                                        payloadToken.job_id,
                                                        payloadToken.user_id,
                                                        applicantStatus
                                                    );
                                                }
                                            }}
                                            hasFormAssessment={hasFormAfterInterview}
                                            hasEligibilityCheck={hasEligibilityAfterInterview}
                                        />
                                    ) : (
                                    <DailyProvider callObject={callObject}>
                                        <SpeechDetectionProvider
                                            volumeThreshold={30}
                                            silenceDelay={400}
                                            autoStart={true}
                                        >
                                            <CustomVoiceInterviewV2
                                                interviewId={interviewAssessment.id as string}
                                                pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || ""}
                                                agentName={import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || ""}
                                                authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || ""}
                                                s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || ""}
                                                s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || ""}
                                                awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || ""}
                                                fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                                                userId={payloadToken.user_id || ''}
                                                userCV={formatCV(cvData.CV_data)}
                                                userName={cvData.CV_data.name || ''}
                                                isSkipInterview={false}
                                                isLoadingSkipInterview={false}
                                                sections={formattedSections}
                                                interviewName={interviewAssessment.content.interviewName}
                                                description={interviewAssessment.content.description}
                                                maxDuration={interviewAssessment.content.maxDuration || 20}
                                                interviewContext={interviewAssessment.content.interviewContext || "You are an interviewer for a job position."}
                                                closingStatement={interviewAssessment.content.closingStatement || ""}
                                                interviewerPersona={interviewAssessment?.content.interviewerPersona || ""}
                                                toneAndCommunication={interviewAssessment?.content.toneAndCommunication || undefined}
                                                interviewFlowPrinciples={interviewAssessment?.content.interviewFlowPrinciples || undefined}
                                                informationToShare={interviewAssessment?.content.informationToShare || undefined}
                                                customGreeting={interviewAssessment.content.customGreeting || ""}
                                                job_id={jobDetail.id}
                                                company_id={jobDetail.company_id}
                                                interviewLanguage={interviewAssessment.content.interviewLanguage || "en-US"}
                                                hasEligibilityCheck={hasEligibilityAfterInterview}
                                                voice={interviewAssessment.content.voice || "male"}
                                                idleTimeoutMessage={interviewAssessment.content.idleTimeoutMessage}
                                                onCloseModal={() => {
                                                    handleCheckInterviewScore();
                                                }}
                                                onCompletedInterview={onCompleteInterview}
                                                onInterviewStatusChange={(status: string) => {
                                                    interviewStatusRef.current = status;
                                                    setCurrentInterviewStatus(status);
                                                    if (status !== "Complete") {
                                                        let applicantStatus: string = "";
                                                        if (status === interviewStatusComplete) {
                                                            applicantStatus = "Pending Review";
                                                        } else if (status === interviewStatusPartial) {
                                                            applicantStatus = "Partial Interview";
                                                        } else if (status === interviewStatusAborted) {
                                                            applicantStatus = "Aborted Interview";
                                                        } else {
                                                            applicantStatus = "Interview Incomplete";
                                                        }
                                                        updateApplicantStatusAssessmentCompletion(
                                                            payloadToken.job_id,
                                                            payloadToken.user_id,
                                                            applicantStatus
                                                        );
                                                    }
                                                }}
                                                hasFormAssessment={hasFormAfterInterview}
                                            />
                                        </SpeechDetectionProvider>
                                    </DailyProvider>
                                    )}
                                </div>
                            )}
                            </div>
                        </div>
                    );
                } else {
                    if (interviewCompleted) {
                        if (isFlowManaged) {
                            return renderFlowProcessingCard();
                        }
                        handleNextStep();
                        return null;
                    }

                    // If no custom interview assessment, show AI Interview
                    return (
                        <div className="container mx-auto max-w-6xl h-full justify-center align-middle items-center sm:flex flex-col">
                            <div className="m-4 sm:m-0">
                                <DailyProvider callObject={callObject}>
                                    <SpeechDetectionProvider
                                        volumeThreshold={30}
                                        silenceDelay={400}
                                        autoStart={true}
                                    >
                                        <VoiceInterviewV2
                                            pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL}
                                            agentName={import.meta.env.VITE_PIPECAT_AGENT_NAME}
                                            authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN}
                                            interviewContext={INTERVIEW_CONFIG.conversationalContext}
                                            s3BucketName={import.meta.env.VITE_AWS_S3_BUCKET_NAME}
                                            s3BucketRegion={import.meta.env.VITE_AWS_S3_BUCKET_REGION}
                                            awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN}
                                            conversationName={INTERVIEW_CONFIG.conversationName}
                                            fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL}
                                            onCloseModal={handleCheckInterviewScore}
                                            onCompletedInterview={onCompleteInterview}
                                            customGreeting={REPLICA_CONFIG.greeting}
                                            userId={payloadToken.user_id || ""}
                                            userName={getFirstName(cvData?.CV_data?.name as string) || ""}
                                            onInterviewStatusChange={(status: string) => {
                                                interviewStatusRef.current = status;
                                                setCurrentInterviewStatus(status);
                                                if (status !== "Complete") {
                                                    let applicantStatus: string = "";
                                                    if (status === interviewStatusComplete) {
                                                        applicantStatus = "Pending Review";
                                                    } else if (status === interviewStatusPartial) {
                                                        applicantStatus = "Partial Interview";
                                                    } else if (status === interviewStatusAborted) {
                                                        applicantStatus = "Aborted Interview";
                                                    } else {
                                                        applicantStatus = "Interview Incomplete";
                                                    }
                                                    updateApplicantStatusAssessmentCompletion(
                                                        payloadToken.job_id,
                                                        payloadToken.user_id,
                                                        applicantStatus
                                                    );
                                                }
                                            }}
                                        />
                                    </SpeechDetectionProvider>
                                </DailyProvider>
                            </div>
                        </div>
                    );
                }

            case 3:
                if (isFlowManaged) {
                    // Wait for the CURRENT step's form to be mounted (a custom
                    // flow can hold several forms); completion is handled by
                    // the polling effect, not a render-phase advance (#3077).
                    if (!assessment || (flowCurrent?.assessment_id && assessment.id !== flowCurrent.assessment_id) || isAssessmentFormCompleted) {
                        return renderFlowProcessingCard();
                    }
                } else if (!assessment || isAssessmentFormCompleted) {
                    handleNextStep();
                    return null;
                }

                return (
                    <div className="container mx-auto px-0 sm:px-4 pt-0 sm:pt-10 pb-16 max-w-4xl">
                        <div className="m-4 sm:m-0">
                            <Quiz
                                surveyCompleted={surveyCompleted}
                                assessment={assessment as FormAssessment}
                                jobId={jobDetail?.id as string}
                                onComplete={() => {
                                    setSurveyCompleted(true);
                                    // #4512: the submit request already judged any knockout
                                    // gate server-side — pull the fresh status so a flagged
                                    // candidate sees the paused screen right away.
                                    refreshApplicantStatus();
                                    handleNextStep();
                                }}
                                onBack={() => handlePrevStep()}
                            />
                        </div>
                    </div>
                )

            case 4:
                // #3328: already-submitted answers — don't re-ask (a re-submit
                // would overwrite them). The advance itself runs in an effect
                // (state updates don't belong in render); this branch only
                // keeps the survey unmounted for the frame(s) until it fires.
                // Flow-managed jobs never reach this: the server-driven runner
                // doesn't re-serve a completed step.
                if (!isFlowManaged && eligibilitySubmitted) {
                    return null;
                }
                return (
                    <div className="container mx-auto px-0 sm:px-4 pt-0 sm:pt-10 pb-16 max-w-4xl">
                        <div className="m-4 sm:m-0">
                            <SWEExperienceSurvey
                                job={jobDetail as Job}
                                onComplete={(application) => {
                                    // #3463: the /jobs/apply response itself carries the
                                    // post-gate status — react to it NOW instead of waiting
                                    // for the pending-assessments refetch. Also capture the
                                    // application id: the applicant/status load response has
                                    // no application_id today, so this may be the first time
                                    // we learn it (unblocks refreshFlow for the session).
                                    const paused = toPauseState(application?.status);
                                    if (paused) setSubmitPausedState(paused);
                                    if (application?.id && !applicationId) {
                                        setApplicationId(application.id);
                                    }
                                    handleNextStep();
                                    sessionStorage.removeItem("__referral_code__");
                                }}
                            />
                        </div>
                    </div>
                )

            case 5:
                return (
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div className="mx-auto h-full justify-center align-middle items-center flex">
                                <div className="p-6 sm:p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6 w-full"
                                    style={{
                                        boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                    }}
                                >
                                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-green-100 text-green-700 mx-auto">
                                        <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                    </div>
                                    <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-center">
                                        Screening Completed!
                                    </h1>
                                    <p className="text-gray-600 dark:text-gray-400 text-center">
                                        Thank you for completing your AI screening with ourteam.
                                        <br />
                                        <br />
                                        You will be notified of your application status via email.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                );
        }
    };

    const progressSteps: ProgressStep[] = [
        {
            label: "AI Interview",
            isActive: currentStep === 2,
            isCompleted: currentStep > 2,
            isHide: false,
        },
        {
            label: "Form",
            isActive: currentStep === 3,
            isCompleted: currentStep > 3,
            isHide: !assessment,
        },
        {
            label: "Eligibility Check",
            isActive: currentStep === 4,
            isCompleted: currentStep > 4,
            isHide: jobDetail?.is_enable_work_authorization ? false : true,
        },
        {
            label: "Complete",
            isActive: currentStep === 5,
            isCompleted: currentStep > 5,
        },
    ];

    let newCurrentStep = currentStep;
    if (!assessment && currentStep === 4) {
        newCurrentStep = currentStep - 1;
    }

    // Flag-driven order: the same three screens + Complete, listed and
    // completed in the job's hiring_flow order.
    const flagOrderPosition = currentStep === 5 ? flagFlowOrder.length : Math.max(0, flagFlowOrder.indexOf(currentStep));
    const flagOrderProgressSteps: ProgressStep[] = useFlagFlowOrder
        ? [
            ...flagFlowOrder.map((screen, i) => ({
                label: legacyScreenLabel(screen),
                isActive: currentStep === screen,
                isCompleted: flagOrderPosition > i,
                isHide: (screen === 3 && !assessment) || (screen === 4 && !jobDetail?.is_enable_work_authorization),
            })),
            { label: "Complete", isActive: currentStep === 5, isCompleted: false },
        ]
        : [];

    // Flow-managed progress (#3077): the server list carries the REMAINING
    // steps in order (completed steps are not returned), plus a trailing
    // Complete marker.
    const flowProgressSteps: ProgressStep[] = isFlowManaged && flowInfo
        ? [
            ...flowInfo.steps.map((s) => ({
                label: s.label,
                isActive: !!flowCurrent && s.step_id === flowCurrent.step_id && currentStep !== 5,
                isCompleted: s.status === "completed" || currentStep === 5,
                isHide: false,
            })),
            { label: "Complete", isActive: currentStep === 5, isCompleted: false },
        ]
        : [];
    const flowCurrentIndex = isFlowManaged && flowInfo
        ? (flowCurrent
            ? Math.max(0, flowInfo.steps.findIndex((s) => s.step_id === flowCurrent.step_id))
            : flowInfo.steps.length)
        : 0;

    const isInvalidAssessment = !isLoadingApplicantStatus && !isLoadingJobDetail && (isJobNotFound || applicantStatus === "Archived" || applicantStatus === "Rejected");

    // Hold the entire render until the pre-load of interview status finishes.
    // Without this gate the welcome screen would briefly render before
    // currentInterviewStatus arrives, then snap to InterviewShortCard /
    // completed-state — jarring UX. Only blocks when the pre-load is actually
    // applicable (valid token, valid applicant). Invalid states fall through
    // so their own dedicated screens (rejected/expired/etc.) still render.
    const shouldBlockOnInterviewStatusPreload =
        isPreloadingInterviewStatus &&
        !isInvalidAssessment &&
        !!payloadToken?.user_id && !!payloadToken?.job_id && !!payloadToken?.expired_date;
    if (shouldBlockOnInterviewStatusPreload) {
        return (
            <SnackbarProvider>
                <div className="h-[calc(100vh-80px)] flex items-center justify-center">
                    <Loader2Icon className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            </SnackbarProvider>
        );
    }

    // Applicant has finished the whole assessment (interview + any required
    // form / eligibility steps) and is now in the reviewer's funnel — show
    // the "Assessment Completed" card directly. Gated on funnel_status from
    // the backend (single source of truth for "candidate is done"), not on
    // interview-level or applicant-level status which can lag behind.
    // #3463: a gate paused the flow — the application is with the hiring team.
    // Neutral, calm copy only: the candidate is under review, not rejected
    // (gates never auto-reject; HR decides), so no failure/knockout wording and
    // no retry affordance (there is nothing the candidate can do). The deadline
    // variant is honest about WHY it stopped — the candidate lived through the
    // deadline, so "thanks for completing this step" would read as a mistake.
    // Server-owned state: a reload lands back here; once HR waives/extends, the
    // next fetch returns an active flow and the runner resumes at the paused
    // step. An archived/rejected candidate (HR resolved by archiving) keeps the
    // dedicated invalid screen instead — "under review" would be a lie there.
    if (isFlowPaused && !isInvalidAssessment) {
        const teamSuffix = jobDetail?.company_name ? ` at ${jobDetail.company_name}` : "";
        return (
            <SnackbarProvider>
                <div className="h-[calc(100vh-80px)] overflow-y-auto">
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="p-6 sm:p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6 w-full"
                                style={{ boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)' }}
                            >
                                {isFlowDeadlineMissed ? (
                                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-amber-100 text-amber-700 mx-auto">
                                        <CalendarClockIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                    </div>
                                ) : (
                                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto">
                                        <ClockIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                    </div>
                                )}
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-center">
                                    {isFlowDeadlineMissed
                                        ? "The deadline for this step has passed"
                                        : "Your application is under review"}
                                </h1>
                                {isFlowDeadlineMissed ? (
                                    <p className="text-gray-600 dark:text-gray-400 text-center">
                                        Your application is with the hiring team{teamSuffix}.
                                        {" "}We will email you if the deadline is extended.
                                    </p>
                                ) : (
                                    <p className="text-gray-600 dark:text-gray-400 text-center">
                                        The hiring team{teamSuffix} will be reviewing your responses, there is no action needed for now.
                                        <br /><br />
                                        Any updates to your application will be sent via email.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </SnackbarProvider>
        );
    }

    if (funnelStatus === "to_review") {
        return (
            <SnackbarProvider>
                <div className="h-[calc(100vh-80px)] overflow-y-auto">
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="p-6 sm:p-8 flex flex-col rounded-3xl bg-white items-center justify-center gap-6 w-full"
                                style={{ boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)' }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-green-100 text-green-700 mx-auto">
                                    <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-center">
                                    Screening Completed!
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 text-center">
                                    Thank you for completing your AI screening with ourteam.
                                    <br /><br />
                                    You will be notified of your application status via email.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </SnackbarProvider>
        );
    }

    const isTokenExpired = !!(payloadToken?.expired_date && new Date(payloadToken.expired_date) < new Date());
    // Default to expired when the token date is past, unless the API explicitly
    // confirms the link is still valid. Keeps the hard gate when the signal is
    // absent (null on initial load, API failure, old backend, Redis down, etc.).
    const isLinkExpired = isTokenExpired && isAssessmentLinkValid !== true;

    if (!isStartInterview && !isInvalidAssessment && !(!payloadToken || !payloadToken.user_id || !payloadToken.job_id || !payloadToken.expired_date) && !isLinkExpired && !isAssessmentResent) {
        const isAnyLoading = isLoadingJobDetail || isLoadingApplicantStatus;
        const hasForm = !!jobDetail?.assessment_ids?.find(a => a.type === "Form");
        const hasEligibility = !!jobDetail?.is_enable_work_authorization;
        const aiInterviewMins = Math.round((interviewAssessment?.content?.maxDuration || 1200) / 60);
        const formQuestionCount = assessment?.content?.questions?.length ?? 0;
        const eligibilityQuestionCount = getEligibilityQuestionCount(jobDetail?.work_authorization_metadata);

        // Flow-managed jobs (#3077): the "what to expect" list mirrors the
        // server's step order instead of the hardcoded interview→form→eligibility.
        const flowPillMeta: Record<string, { time: string; tooltip: string }> = {
            ai_interview: { time: formatWelcomeMins(aiInterviewMins), tooltip: "A conversational AI interview to learn about you beyond your CV." },
            form_assessment: { time: "±5 min", tooltip: "A short questionnaire set by the hiring team." },
            eligibility_check: { time: "±2 min", tooltip: "Additional questions related to your employment status." },
            external_link: { time: "±5 min", tooltip: "Complete a short task on an external page, then confirm here." },
            documents: { time: "±10 min", tooltip: "Upload the documents the hiring team requires." },
            human_interview: { time: "Scheduled", tooltip: "The hiring team will schedule this interview with you." },
        };
        const welcomeStepPills: WelcomeStepPill[] = isFlowManaged && flowInfo && flowInfo.steps.length > 0
            ? flowInfo.steps.map((s) => ({
                label: s.label,
                time: flowPillMeta[s.type]?.time || "",
                tooltip: flowPillMeta[s.type]?.tooltip || "",
                wrapTooltip: s.type === "ai_interview",
            }))
            // Flag-order jobs list the same three steps in the hiring_flow
            // order; legacy jobs keep the hardcoded interview → form → eligibility.
            : (useFlagFlowOrder ? flagFlowOrder : [2, 3, 4]).flatMap((screen): WelcomeStepPill[] => {
                if (screen === 2) return [{
                    label: "AI Interview",
                    time: formatWelcomeMins(aiInterviewMins),
                    tooltip: "A conversational AI interview to learn about you beyond your CV.",
                    wrapTooltip: true,
                }];
                if (screen === 3) return hasForm ? [{
                    label: "Form",
                    time: formatWelcomeMins(Math.max(1, Math.ceil((formQuestionCount * 30) / 60))),
                    tooltip: "A short questionnaire set by the hiring team.",
                }] : [];
                return hasEligibility ? [{
                    label: "Eligibility Check",
                    time: formatWelcomeMins(Math.max(1, Math.ceil((eligibilityQuestionCount * 10) / 60))),
                    tooltip: "Additional questions related to your employment status.",
                }] : [];
            });

        const totalSteps = welcomeStepPills.length;
        const boldPart = `${totalSteps} step${totalSteps > 1 ? "s" : ""}`;
        const companyDisplay = jobDetail?.company_name !== jobDetail?.main_company_name && jobDetail?.main_company_name
            ? `${jobDetail.company_name} by ${jobDetail.main_company_name}`
            : jobDetail?.company_name ?? "";
        const daysLeft = parseInt(countDayNowBetweenDate(payloadToken.expired_date as string) || "0");
        const candidateName = getFirstName(cvData?.CV_data?.name as string) || "there";

        return (
            <>
                <div
                    className="container mx-auto px-4 py-6 sm:py-8 flex justify-center items-start sm:items-center max-w-[1160px]"
                    style={{ minHeight: "calc(100vh - 80px)" }}
                >
                    <Card className="w-full border-0 shadow-none">
                        <CardContent className="p-0 overflow-hidden">
                            <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-neutral-100">
                                {/* Left panel */}
                                <div className="flex-1 flex flex-col gap-5 sm:gap-7 p-1 sm:p-8 lg:p-10">
                                    <div className="space-y-2">
                                        <h2 className="text-xl sm:text-[1.65rem] font-bold text-gray-900">
                                            Hey {candidateName}, ready to begin your quick AI screening?
                                        </h2>
                                        <p className="text-sm sm:text-base text-neutral-600 leading-relaxed">
                                            This screening has <strong className="text-gray-900 font-semibold">{boldPart}</strong> to complete.
                                        </p>
                                    </div>

                                    <WelcomeJobCard
                                        jobTitle={jobDetail?.job_title ?? ""}
                                        companyName={jobDetail?.company_name ?? ""}
                                        companyDisplay={companyDisplay}
                                        logoUrl={jobDetail?.company_logo ? `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${jobDetail.company_logo}` : null}
                                        daysLeft={daysLeft}
                                        expiryDate={parseFormat(payloadToken.expired_date, "D MMMM YYYY")}
                                        jobId={jobDetail?.id ?? ""}
                                        invitedDate={applicantAppliedDate ? parseFormat(applicantAppliedDate, "D MMMM YYYY") : ""}
                                        department={jobDetail?.department ?? ""}
                                        isLoading={isAnyLoading}
                                    />

                                    <div className="space-y-4 hidden lg:block">
                                        <div className="flex items-center gap-3">
                                            <Checkbox
                                                id="terms"
                                                checked={termsAccepted}
                                                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                                            />
                                            <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                                                By proceeding, you agree to the{" "}
                                                <a
                                                    href="https://ourteam.ai/terms-of-service"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="underline text-blue-600 hover:text-black"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Terms of Service
                                                </a>{" "}
                                                at ourteam.
                                            </label>
                                        </div>
                                        <Button
                                            onClick={startInterview}
                                            disabled={isLoadingStartInterview || isLoadingJobDetail || isLoadingCV || isLoadingCompanyInfo || isLoadingAssessment || isLoadingAIInterview || isLoadingInterviewStatus || isLoadingApplicantStatus || !termsAccepted}
                                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                            style={{ paddingTop: "16px", paddingBottom: "16px" }}
                                        >
                                            {isLoadingStartInterview ? (
                                                <>
                                                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                                    Preparing your screening...
                                                </>
                                            ) : (
                                                <>
                                                    Begin Screening
                                                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                {/* Right panel — What to expect */}
                                <div className="w-full lg:w-96 lg:flex-shrink-0 bg-white p-1 sm:p-8 lg:p-10 flex flex-col gap-5 mt-4 pt-4 sm:pt-4 sm:mt-0">
                                    <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">What to expect</p>

                                    {/* Mobile: card-based steps */}
                                    <div className="flex lg:hidden flex-col gap-3">
                                        {welcomeStepPills.map((step, i) => (
                                            <div key={step.label} className="bg-white border border-gray-200 rounded-xl p-3.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                        {i + 1}
                                                    </div>
                                                    <p className="text-sm font-semibold text-gray-900">{step.label}</p>
                                                    <span className="text-xs font-medium text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">{step.time}</span>
                                                </div>
                                                <p className="text-xs text-neutral-500 leading-relaxed mt-0.5 pl-9">{step.tooltip}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Desktop: vertical timeline */}
                                    <div className="hidden lg:flex flex-col">
                                        {welcomeStepPills.map((step, i) => (
                                            <div key={step.label} className="flex gap-3.5">
                                                <div className="flex flex-col items-center flex-shrink-0">
                                                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                                        {i + 1}
                                                    </div>
                                                    {i < welcomeStepPills.length - 1 && (
                                                        <div className="w-px flex-1 bg-neutral-200 my-1.5" />
                                                    )}
                                                </div>
                                                <div className={i < welcomeStepPills.length - 1 ? "pb-7" : ""}>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-lg font-semibold text-gray-900">{step.label}</span>
                                                        <span className="text-xs font-medium text-blue-500 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">{step.time}</span>
                                                    </div>
                                                    <p className="text-sm text-neutral-500 leading-relaxed">{step.tooltip}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="space-y-4 block lg:hidden">
                                        <div className="flex items-center gap-3">
                                            <Checkbox
                                                id="terms"
                                                checked={termsAccepted}
                                                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                                            />
                                            <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                                                By proceeding, you agree to the{" "}
                                                <a
                                                    href="https://ourteam.ai/terms-of-service"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="underline text-blue-600 hover:text-black"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    Terms of Service
                                                </a>{" "}
                                                at ourteam.
                                            </label>
                                        </div>
                                        <Button
                                            onClick={startInterview}
                                            disabled={isLoadingStartInterview || isLoadingJobDetail || isLoadingCV || isLoadingCompanyInfo || isLoadingAssessment || isLoadingAIInterview || isLoadingInterviewStatus || isLoadingApplicantStatus || !termsAccepted}
                                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                                            style={{ paddingTop: "16px", paddingBottom: "16px" }}
                                        >
                                            {isLoadingStartInterview ? (
                                                <>
                                                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                                                    Preparing your screening...
                                                </>
                                            ) : (
                                                <>
                                                    Begin Screening
                                                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Fixed bottom progress bar */}
                {jobDetail && (
                    <div className="w-full fixed bottom-0 left-0 right-0 px-4 pt-4 pb-4 bg-white shadow-sm hidden sm:block">
                        <ProgressBar
                            currentStep={0}
                            steps={isFlowManaged && flowProgressSteps.length > 0
                                ? flowProgressSteps.map((s) => ({ ...s, isActive: false, isCompleted: false }))
                                : [
                                    ...(useFlagFlowOrder ? flagFlowOrder : [2, 3, 4]).map((screen) => ({
                                        label: legacyScreenLabel(screen),
                                        isActive: false,
                                        isCompleted: false,
                                        isHide: (screen === 3 && !hasForm) || (screen === 4 && !hasEligibility),
                                    })),
                                    { label: "Complete", isActive: false, isCompleted: false },
                                ]}
                        />
                    </div>
                )}
            </>
        );
    }

    return (
        <SnackbarProvider>
            <div className="h-[calc(100vh-80px)] overflow-y-auto">
                {(isRequestedNewLink) ? (
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto mb-6">
                                    <Link2 className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                                    New Interview Link Requested
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                    Your request has been submitted. Once approved by our HR team, <br />  you will receive a new interview link via email.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : ((!isLoadingJobDetail && isJobNotFound) || (isWorkspaceGraceExpired && assessmentInfoStatus !== "Pending Assessment")) ? (
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-red-100 text-red-700 mx-auto mb-6">
                                    <Link2OffIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                                    This position is now closed.
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                    The hiring team is no longer accepting applications for this role. For questions about your application, please contact them directly.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (!isLoadingApplicantStatus && (applicantStatus === "Archived" || applicantStatus === "Rejected")) ? (
                            <div className="container mx-auto max-w-2xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-red-100 text-red-700 mx-auto mb-6">
                                    <Link2OffIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                                    This screening is no longer available.
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                            The hiring team has closed this link. <br /> Please check your email for an update on your application, <br /> or reach out to them directly if you haven't heard back.
                                </p>
                            </div>
                        </div>
                    </div>
                        ) : (!isLoadingApplicantStatus && funnelStatus === "to_review") ? (
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-green-100 text-green-700 mx-auto mb-6">
                                    <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-center">
                                    Screening Completed!
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 text-center">
                                    Thank you for completing the <b>{interviewAssessment?.content?.interviewName}</b> with ourteam.
                                    <br />
                                    You will be notified of your application status via email.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    assessmentInfoError
                    || (isWorkspaceGraceExpired && assessmentInfoStatus === "Pending Assessment")
                    || (!payloadToken || !payloadToken.user_id || !payloadToken.job_id || !payloadToken.expired_date)
                ) ? (
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-red-100 text-red-700 mx-auto mb-6">
                                    <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                                    Invalid Interview Link
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                    <>
                                        The interview link you used is invalid.
                                        <br />
                                        Please contact <b>support@ourteam.ai</b> for assistance.
                                    </>
                                </p>
                            </div>
                        </div>
                    </div>
                ) : isAssessmentResent ? (
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 mx-auto mb-6">
                                    <Mail className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                                    A newer link has been sent
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mb-6 mx-auto whitespace-pre-line">
                                    This interview link was replaced by a newer one.
                                </p>
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/40 text-blue-700 dark:text-blue-300 rounded-xl px-6 py-4 mb-6 text-sm leading-relaxed">
                                                            Please check your email inbox <b>and spam folder</b> <br /> for the latest link from ourteam.
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Can't find it? Email{" "}
                                    <a href="mailto:support@ourteam.ai" className="underline hover:text-blue-600 dark:hover:text-blue-400">
                                        support@ourteam.ai
                                    </a>.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : isLinkExpired ? (
                    <div className="container mx-auto max-w-xl h-full justify-center align-middle items-center flex">
                        <div className="m-4 sm:m-0">
                            <div
                                className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl text-center w-full mx-auto"
                                style={{
                                    boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
                                }}
                            >
                                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-red-100 text-red-700 mx-auto mb-6">
                                    <Link2OffIcon className="h-6 sm:h-10 w-6 sm:w-10" />
                                </div>
                                <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                                    Interview Link Expired
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400 mb-8 mx-auto whitespace-pre-line">
                                    Your interview link has expired.
                                    <br />
                                    Please contact <b>support@ourteam.ai</b> for assistance.
                                </p>
                                <Button
                                    variant="default"
                                    className="w-full sm:w-auto bg-blue-600 flex-1"
                                    onClick={onRequestNewLink}
                                    disabled={isRequestingNewLink}
                                >
                                    {isRequestingNewLink ? "Requesting..." : "Request New Link"}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {renderCurrentStep()}

                        <div className="w-full fixed bottom-0 left-0 right-0 px-4 pt-6 sm:pt-4 pb-6 sm:pb-4 bg-white shadow-sm">
                            <ProgressBar
                                currentStep={isFlowManaged ? flowCurrentIndex : useFlagFlowOrder ? flagOrderPosition + 1 : newCurrentStep - 1}
                                isShowBanding={companyInfo?.is_show_brand}
                                companyPreferences={companyInfo?.preferences}
                                steps={isFlowManaged ? flowProgressSteps : useFlagFlowOrder ? flagOrderProgressSteps : progressSteps}
                            />
                        </div>
                    </>
                )}
            </div>
        </SnackbarProvider >
    );
}
